import React, { useContext, useEffect, useState, useRef } from "react"
import { TextInput, View, Text, StyleSheet, TouchableWithoutFeedback } from "react-native"
import { ThemeContext } from "../utility/context/ThemeContext"
import { textStyles } from "../style"
import { ThemedButton } from "../utility/input/buttons"
import { COUNTRY_CODES, PHONE_REGEX } from "@ecountabl/lib"
import api from "../api-client"
import { StackActions, useNavigation } from "@react-navigation/native"
import { Formik } from "formik"
import PhoneNumberInput from "../utility/phoneNumberInput"
import { MaterialCommunityIcons } from "@expo/vector-icons"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import Analytics from "../utility/analytics"
import { MAX_CONTENT_WIDTH } from "../config"

type Props = {
  route: {
    params: {
      token?: string
      state?: string
      theme?: string
    }
  }
}

const MultiFactorSelect = ({ route }: Props): React.ReactElement<Props> => {
  const { theme, setTheme } = useContext(ThemeContext)
  const { textMain, bgMain } = theme.colors

  const navigation = useNavigation()
  const { top, bottom } = useSafeAreaInsets()

  const [sending, setSending] = useState<boolean>(false)

  const inputRef = useRef<TextInput>(null)

  const securityMessage = [
    "Security is a top priority for us. To ensure your account is as secure as possible we",
    "require all accounts to configure a their SMS Multi-Factor Authentication (MFA) method."
  ].join(" ")

  // handle params
  useEffect(() => {
    const params = route.params
    // if theme is provided, set it
    if (params?.theme) {
      setTheme(params.theme)
    }

    // if no token defined, redirect to login
    if (!params?.token) {
      navigation.dispatch(StackActions.replace("Login"))
    }
  }, [navigation, route.params, setTheme])

  // AUTO-SCROLL INPUT INTO VIEW ON MOBILE WEB
  useEffect(() => {
    const handler = (e: any) => {
      const target = e.target
      if (!target?.scrollIntoView) return

      setTimeout(() => {
        target.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      }, 250)
    }

    document.addEventListener("focusin", handler)
    return () => document.removeEventListener("focusin", handler)
  }, [])

  return (
    <TouchableWithoutFeedback
      accessible={false}
      onPress={() => {
        inputRef.current?.blur()
      }}
    >
      <View
        style={{
          flex: 1,
          backgroundColor: bgMain,
          justifyContent: "center",
          alignItems: "center",
          paddingTop: top || 16,
          paddingBottom: bottom || 16,
          paddingHorizontal: 16,
        }}
      >
        <View style={{ width: "100%", maxWidth: MAX_CONTENT_WIDTH }}>
        
          {/* Content wrapper */}
          <View style={{ gap: 10, width: "100%" }}>
            <Text style={[textStyles.h3, { color: textMain, textAlign: "center" }]}>
              Welcome to Impact Banking
            </Text>

            <View style={{ width: "100%", alignItems: "center" }}>
              <MaterialCommunityIcons name="shield-account" color={textMain} size={150} />
            </View>

            <Text style={[textStyles.h4, { color: textMain, textAlign: "center" }]}>
              {securityMessage}
            </Text>

            <Text style={[textStyles.h4, { color: textMain, textAlign: "center" }]}>
              Please enter your phone number below to receive a verification code.
            </Text>
          </View>

          {/* Form Section */}
          <View style={{ gap: 10, width: "100%" }}>
            <Formik
              initialValues={{
                phone: "",
                countryCode: (() => {
                  const usCountry = COUNTRY_CODES.find(c => c.code === "US")
                  return `${usCountry?.code} ${usCountry?.dial_code}`
                })(),
              }}
              validate={values => {
                if (!values.phone) return { phone: "Phone number is required" }
                if (!PHONE_REGEX.test(values.phone)) return { phone: "Invalid phone number format" }
                return null
              }}
              onSubmit={async (values, actions) => {
                const phone = `${values.countryCode.split(" ")[1]}${values.phone}`
                let succeeded = false

                try {
                  setSending(true)
                  api.setToken(route.params?.token)

                  const res = await api.triggerMFA("sms", phone)
                  succeeded = true

                  navigation.dispatch(
                    StackActions.push("VerifyMfa", {
                      shorthand: res.phone,
                      state: route.params.state,
                      token: route.params.token
                    })
                  )
                } catch (e) {
                  actions.setErrors({
                    phone: e.message || "An error occurred while sending the phone number"
                  })
                } finally {
                  Analytics.track("mfa_send_code", { success: succeeded })
                  setSending(false)
                }
              }}
            >
              {({ handleSubmit, values, errors, touched, setValues }) => (
                <View style={{ gap: 10 }}>
                  
                  {/* Phone Input */}
                  <PhoneNumberInput
                    inputRef={inputRef}
                    value={{ countryCode: values.countryCode, phoneNumber: values.phone }}
                    onChange={(countryCode, phoneNumber) => {
                      setValues({
                        phone: phoneNumber,
                        countryCode: countryCode,
                      })
                    }}
                    placeholder="Phone Number"
                    error={touched.phone && errors.phone ? errors.phone : undefined}
                  />

                  {/* Submit Button */}
                  <View style={styles.submitButtonWrapper}>
                    <ThemedButton
                      text="Send Verification Code"
                      onPress={handleSubmit}
                      loading={sending}
                      disabled={sending || !values.phone || !!errors.phone}
                      variant="primary"
                    />
                  </View>

                </View>
              )}
            </Formik>
          </View>
        </View>
      </View>
    </TouchableWithoutFeedback>
  )
}

export default MultiFactorSelect

const styles = StyleSheet.create({
  submitButtonWrapper: {
    paddingHorizontal: 16,
    paddingTop: 16,
  },
})
